/**
 * #89 · Listado de etiquetas. #159 · Ahora también de la marca entera.
 *
 * Sin nada en la URL, la pantalla lista TODAS las etiquetas de la marca, de
 * la más nueva a la más vieja. Hasta #159 esto no existía y la pantalla caía
 * al lote más reciente: no porque `unit` sea grande —300M en producción— sino
 * porque no había índice que sirviera `WHERE tenant_id ORDER BY id DESC`, y
 * la consulta recorría la pkey al revés descartando las filas de los otros
 * tenants (411 ms sobre 2M; cuatro MINUTOS sobre 30M para un cliente con ids
 * viejos). Con `unit_tenant_keyset_idx` (migración 0029) cada página es un
 * recorrido de índice de cincuenta entradas, y el 400 `label_scope_required`
 * desapareció de la API.
 *
 * Los tres ámbitos —lote, rollo, código— siguen ahí como filtros: son los
 * puntos de partida de una investigación y cada uno tiene su propio índice.
 * El ámbito vive en la URL (`?scope=batch&value=12`) y no en el estado del
 * componente: un analista que encuentra algo pega el link en el caso, y quien
 * lo abre tiene que ver la misma lista. Es el mismo criterio que usa
 * `RollsPage` para su filtro de estado.
 *
 * Los filtros (#162)
 * ------------------
 * Producto, veredicto, escaneada, vence antes de, con alertas abiertas — más
 * el estado que ya estaba. Todos viajan en la URL y todos los aplica **el
 * servidor** sobre el listado paginado: un filtro que sólo mirara la página
 * cargada diría "3 resultados" y el analista concluiría que hay tres. La
 * pantalla no ofrece ninguno que la API no aplique; los rangos de fecha de la
 * referencia no están porque la API no los tiene, y no los tiene porque cada
 * uno es un índice de ~9 GB sobre la tabla del camino caliente.
 *
 * Sobre el "total" que no está
 * ---------------------------
 * La paginación es por keyset y no cuenta filas: un `COUNT(*)` sobre una
 * marca de un millón de unidades cuesta lo mismo que la consulta que las
 * lista. Así que el contador dice **cuántas se cargaron**, y sólo dice "en
 * total" cuando ya no hay más páginas. Un número inventado arriba de una tabla
 * es peor que ninguno: se cita en una reunión.
 */
import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useKeysetQuery } from '@/api/hooks';
import { labelUnitKeys, productKeys } from '@/api/keys';
import type { Schemas } from '@/api/types';
import { useApi } from '@/api/useApi';
import { useI18n } from '@/i18n/useI18n';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody } from '@/ui/Card';
import { EmptyState, ErrorState, LoadingState } from '@/ui/states';
import { Table, Td, Th } from '@/ui/Table';
import { SelectField, TextField } from '../products/ui/fields';
import { downloadLabelQr } from './assets';
import { LabelsTabs } from './LabelsTabs';
import { StateBadge, VerdictBadge } from './badges';
import {
  UNIT_STATES,
  UNIT_STATE_LABEL_KEYS,
  VERDICT_LABEL_KEYS,
  isScope,
  isUnitState,
  type Scope,
} from './units';

type LabelItem = Schemas['LabelListItem'];
type Product = Schemas['ProductOut'];

const PRODUCT_OPTIONS = 200;
const VERDICTS = Object.keys(VERDICT_LABEL_KEYS);

const PAGE_SIZE = 50;

/**
 * Un código impreso tiene 20 caracteres de un alfabeto sin vocales. Con eso
 * alcanza para que la búsqueda rápida se dé cuenta sola de que le pasaron un
 * código y no el id de un lote, que es lo único ambiguo entre los tres
 * ámbitos.
 */
const PARECE_CODIGO = /^[0-9A-Z]{12,32}$/;

export function LabelUnitsPage(): ReactNode {
  const { t, fmt } = useI18n();
  const api = useApi();
  const [searchParams, setSearchParams] = useSearchParams();

  const rawScope = searchParams.get('scope');
  const scope: Scope = rawScope !== null && isScope(rawScope) ? rawScope : 'all';
  const rawValue = searchParams.get('value') ?? '';
  const rawStatus = searchParams.get('status');
  const status = rawStatus !== null && isUnitState(rawStatus) ? rawStatus : null;
  const rawProduct = searchParams.get('product');
  const productId = rawProduct !== null && /^\d+$/.test(rawProduct) ? Number(rawProduct) : null;
  const rawVerdict = searchParams.get('verdict');
  const verdict = rawVerdict !== null && VERDICTS.includes(rawVerdict) ? rawVerdict : null;
  const rawScanned = searchParams.get('scanned');
  const scanned = rawScanned === 'yes' ? true : rawScanned === 'no' ? false : null;
  const rawExpires = searchParams.get('expires');
  const expiresBefore = rawExpires !== null && /^\d{4}-\d{2}-\d{2}$/.test(rawExpires) ? rawExpires : null;
  const withOpenAlerts = searchParams.get('alerts') === 'open' ? true : null;

  /** Un parámetro de la URL, puesto o quitado. `replace`: un filtro no es un paso de navegación. */
  function setParam(name: string, value: string | null): void {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === '') next.delete(name);
    else next.set(name, value);
    setSearchParams(next, { replace: true });
  }

  // Exportar lo que se está mirando (#175): el mismo ámbito y los mismos
  // filtros viajan al `kind=labels`, así el archivo tiene exactamente las filas
  // de la pantalla. Se ofrece sólo con algo acotado: la marca entera sin
  // filtro son millones de filas y un archivo que nadie abre.
  const hayFiltro =
    status !== null || productId !== null || verdict !== null || scanned !== null ||
    expiresBefore !== null || withOpenAlerts !== null;
  const exportar = useMutation<{ id: number }>({
    mutationFn: () =>
      api.post<{ id: number }>('/api/v1/exports', {
        kind: 'labels',
        ...(batchId === null ? {} : { batch_id: batchId }),
        labels: {
          ...(rollId === null ? {} : { roll_id: rollId }),
          ...(productId === null ? {} : { product_id: productId }),
          ...(status === null ? {} : { status: [status] }),
          ...(verdict === null ? {} : { verdict }),
          ...(scanned === null ? {} : { scanned }),
          ...(expiresBefore === null ? {} : { expires_before: expiresBefore }),
          ...(withOpenAlerts === null ? {} : { with_open_alerts: withOpenAlerts }),
        },
      }),
  });

  const products = useKeysetQuery<Product>(
    productKeys.list({ limit: PRODUCT_OPTIONS }),
    '/api/v1/products',
    undefined,
    { limit: PRODUCT_OPTIONS },
  );

  const [quickFind, setQuickFind] = useState('');
  const [draftScope, setDraftScope] = useState<Scope>(scope);
  const [draftValue, setDraftValue] = useState(rawValue);
  const [qrBusy, setQrBusy] = useState<string | null>(null);
  const [qrError, setQrError] = useState<unknown>(null);

  const numeric = /^\d+$/.test(rawValue) ? Number(rawValue) : null;
  const batchId = scope === 'batch' ? numeric : null;
  const rollId = scope === 'roll' ? numeric : null;
  const code = scope === 'code' && rawValue !== '' ? rawValue.trim().toUpperCase() : null;
  // Un ámbito elegido pero sin valor (`?scope=batch` a secas, o un valor que
  // no es un número) no es "toda la marca": es un formulario a medio llenar,
  // y se espera a que lo completen en vez de listar otra cosa.
  const scopeIncompleto = scope !== 'all' && batchId === null && rollId === null && code === null;

  const labels = useKeysetQuery<LabelItem>(
    labelUnitKeys.list({
      limit: PAGE_SIZE,
      batchId,
      rollId,
      code,
      status,
      productId,
      verdict,
      scanned,
      expiresBefore,
      withOpenAlerts,
    }),
    '/api/v1/labels',
    {
      batch_id: batchId,
      roll_id: rollId,
      code,
      status,
      product_id: productId,
      verdict,
      scanned,
      expires_before: expiresBefore,
      with_open_alerts: withOpenAlerts,
    },
    { limit: PAGE_SIZE, enabled: !scopeIncompleto },
  );
  const { query } = labels;

  function aplicar(nextScope: Scope, nextValue: string): void {
    const next = new URLSearchParams(searchParams);
    // "Toda la marca" es el default y no se escribe: la URL limpia es la
    // pantalla limpia, y el link que se pega en un caso no arrastra un
    // `scope=all` que no dice nada.
    if (nextScope === 'all') {
      next.delete('scope');
      next.delete('value');
    } else {
      next.set('scope', nextScope);
      if (nextValue.trim() === '') next.delete('value');
      else next.set('value', nextValue.trim());
    }
    setSearchParams(next, { replace: true });
  }

  /** La barra de arriba: se da cuenta sola de si le pasaron un código. */
  function buscarRapido(event: FormEvent): void {
    event.preventDefault();
    const valor = quickFind.trim().toUpperCase();
    if (valor === '') return;
    const detectado: Scope = PARECE_CODIGO.test(valor) ? 'code' : draftScope;
    setDraftScope(detectado);
    setDraftValue(valor);
    aplicar(detectado, valor);
  }

  function aplicarFiltros(event: FormEvent): void {
    event.preventDefault();
    aplicar(draftScope, draftValue);
  }

  async function bajarQr(codigo: string): Promise<void> {
    setQrBusy(codigo);
    setQrError(null);
    try {
      await downloadLabelQr(codigo, { api });
    } catch (error) {
      setQrError(error);
    } finally {
      setQrBusy(null);
    }
  }

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      <LabelsTabs active="units" />

      {/* ── el encabezado de la pantalla, fuera de la tarjeta ────────── */}
      <header className="space-y-1">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-display text-2xl font-semibold text-ink">
            {t('labels.units.title')}
          </h1>
          {(hayFiltro || scope !== 'all') && scope !== 'code' && !scopeIncompleto && (
            <span className="ml-auto flex items-center gap-2">
              {exportar.isSuccess ? (
                <Link to="/analytics/exports" className="text-sm text-accent underline underline-offset-2">
                  {t('labels.units.export.queued')}
                </Link>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={exportar.isPending}
                  onClick={() => { exportar.mutate(); }}
                >
                  {exportar.isPending ? t('labels.units.export.sending') : t('labels.units.export')}
                </Button>
              )}
              {exportar.isError && (
                <span className="text-xs text-critical">{t('labels.units.export.failed')}</span>
              )}
            </span>
          )}
          {labels.items.length > 0 && (
            <Badge tone="neutral">
              {labels.hasMore
                ? t('labels.units.count', { count: fmt.number(labels.items.length) })
                : t('labels.units.countTotal', { count: fmt.number(labels.items.length) })}
            </Badge>
          )}
        </div>
        <p className="text-sm text-ink-muted">
          {scope === 'all' && t('labels.units.scoped.all')}
          {scope === 'batch' && t('labels.units.scoped.batch', { value: rawValue })}
          {scope === 'roll' && t('labels.units.scoped.roll', { value: rawValue })}
          {scope === 'code' && t('labels.units.scoped.code', { value: rawValue })}
        </p>
      </header>

      {/* ── búsqueda rápida ─────────────────────────────────────────── */}
      <form onSubmit={buscarRapido} className="flex gap-2">
        <input
          type="search"
          value={quickFind}
          onChange={(e) => {
            setQuickFind(e.target.value);
          }}
          placeholder={t('labels.units.quickFind.placeholder')}
          aria-label={t('labels.units.quickFind.placeholder')}
          className="h-11 flex-1 rounded-(--radius-control) border border-border-strong bg-surface px-3 font-mono text-sm text-ink placeholder:font-sans placeholder:text-ink-subtle focus:border-accent focus:outline-2 focus:outline-accent/40"
        />
        <Button type="submit" variant="primary" size="md">
          {t('labels.units.quickFind.submit')}
        </Button>
      </form>

      {/* ── filtros ─────────────────────────────────────────────────── */}
      <Card>
        <CardBody>
          <form onSubmit={aplicarFiltros} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <SelectField
              label={t('labels.units.scope')}
              hint={t('labels.units.scope.hint')}
              value={draftScope}
              onChange={(value) => {
                if (isScope(value)) setDraftScope(value);
              }}
              options={[
                { value: 'all', label: t('labels.units.scope.all') },
                { value: 'batch', label: t('labels.units.scope.batch') },
                { value: 'roll', label: t('labels.units.scope.roll') },
                { value: 'code', label: t('labels.units.scope.code') },
              ]}
            />
            <TextField
              label={
                draftScope === 'code'
                  ? t('labels.units.scope.value.code')
                  : t('labels.units.scope.value.id')
              }
              value={draftScope === 'all' ? '' : draftValue}
              onChange={setDraftValue}
              disabled={draftScope === 'all'}
              mono
            />
            <div className="flex items-end">
              <Button type="submit" variant="primary" size="md">
                {t('labels.units.scope.apply')}
              </Button>
            </div>
          </form>

          {/* Los filtros de #162, cada uno aplicado en el servidor. Cambian la
              URL al instante: no hay botón porque no hay nada que "aplicar"
              del lado del cliente. */}
          <div className="mt-4 grid gap-4 border-t border-border pt-4 sm:grid-cols-2 lg:grid-cols-5">
            <SelectField
              label={t('labels.units.filter.state')}
              value={status ?? ''}
              onChange={(value) => { setParam('status', value); }}
              options={[
                { value: '', label: t('labels.units.filter.all') },
                ...UNIT_STATES.map((value) => ({
                  value,
                  label: t(UNIT_STATE_LABEL_KEYS[value]),
                })),
              ]}
            />
            <SelectField
              label={t('labels.units.filter.verdict')}
              value={verdict ?? ''}
              onChange={(value) => { setParam('verdict', value); }}
              options={[
                { value: '', label: t('labels.units.filter.all') },
                ...Object.entries(VERDICT_LABEL_KEYS).map(([value, key]) => ({ value, label: t(key) })),
              ]}
            />
            <SelectField
              label={t('labels.units.filter.product')}
              value={productId === null ? '' : String(productId)}
              onChange={(value) => { setParam('product', value); }}
              options={[
                { value: '', label: t('labels.units.filter.all') },
                ...products.items.map((product) => ({
                  value: String(product.id),
                  label: `${product.sku} · ${product.name}`,
                })),
              ]}
            />
            <SelectField
              label={t('labels.units.filter.scanned')}
              value={scanned === null ? '' : scanned ? 'yes' : 'no'}
              onChange={(value) => { setParam('scanned', value); }}
              options={[
                { value: '', label: t('labels.units.filter.all') },
                { value: 'yes', label: t('labels.units.filter.scanned.yes') },
                { value: 'no', label: t('labels.units.filter.scanned.no') },
              ]}
            />
            <SelectField
              label={t('labels.units.filter.alerts')}
              value={withOpenAlerts === true ? 'open' : ''}
              onChange={(value) => { setParam('alerts', value); }}
              options={[
                { value: '', label: t('labels.units.filter.all') },
                { value: 'open', label: t('labels.units.filter.alerts.open') },
              ]}
            />
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-ink">{t('labels.units.filter.expires')}</span>
              <input
                type="date"
                value={expiresBefore ?? ''}
                onChange={(event) => { setParam('expires', event.target.value); }}
                className="h-11 rounded-(--radius-control) border border-border-strong bg-surface px-3 text-sm text-ink focus:border-accent focus:outline-2 focus:outline-accent/40"
              />
              <span className="text-xs text-ink-subtle">{t('labels.units.filter.expires.hint')}</span>
            </label>
          </div>
        </CardBody>
      </Card>

      {/* ── la tabla ────────────────────────────────────────────────── */}
      <Card>
        {/* Un ámbito a medio llenar no consulta nada, y la pantalla lo dice
            en vez de mostrar una tabla vacía: "0 resultados" haría creer al
            analista que la marca no tiene etiquetas. */}
        {scopeIncompleto && (
          <EmptyState
            title={t('labels.units.noScope')}
            detail={t('labels.units.noScope.detail')}
          />
        )}

        {!scopeIncompleto && query.isPending && <LoadingState rows={6} />}

        {!scopeIncompleto && query.isError && (
          <CardBody>
            <ErrorState
              error={query.error}
              onRetry={() => {
                void query.refetch();
              }}
            />
          </CardBody>
        )}

        {!scopeIncompleto && !query.isPending && !query.isError && labels.items.length === 0 && (
          <EmptyState title={t('labels.units.empty')} detail={t('labels.units.empty.detail')} />
        )}

        {labels.items.length > 0 && (
          <>
            {qrError !== null && (
              <CardBody className="border-b border-border">
                <ErrorState error={qrError} />
              </CardBody>
            )}
            <Table caption={t('labels.units.description')}>
              <thead>
                <tr>
                  <Th>{t('labels.units.column.code')}</Th>
                  <Th>{t('labels.units.column.batch')}</Th>
                  <Th>{t('labels.units.column.state')}</Th>
                  <Th>{t('labels.units.column.verdict')}</Th>
                  <Th className="text-right">{t('labels.units.column.scans')}</Th>
                  <Th>{t('labels.units.column.lastScan')}</Th>
                  <Th>{t('labels.units.column.activated')}</Th>
                  <Th>{t('labels.units.column.created')}</Th>
                  <Th className="text-right">
                    <span className="sr-only">{t('labels.units.column.actions')}</span>
                  </Th>
                </tr>
              </thead>
              <tbody>
                {labels.items.map((item) => (
                  <tr key={item.code}>
                    <Td>
                      <Link
                        to={`/labels/units/${item.code}`}
                        className="font-mono text-xs font-medium text-accent underline underline-offset-2"
                      >
                        {item.code}
                      </Link>
                    </Td>
                    <Td className="font-mono text-xs text-ink-muted">
                      {/* Fuera del ámbito por lote la API no resuelve el
                          código de lote (sería un JOIN por fila sobre la
                          tabla grande); queda el enlace al lote por id. */}
                      {item.lot_code ??
                        (item.batch_id === null || item.batch_id === undefined ? (
                          '—'
                        ) : (
                          <Link
                            to={`/batches/${String(item.batch_id)}`}
                            className="text-accent underline underline-offset-2"
                          >
                            {t('labels.units.batchRef', { id: String(item.batch_id) })}
                          </Link>
                        ))}
                    </Td>
                    <Td>
                      <StateBadge state={item.status_name} />
                    </Td>
                    <Td>
                      <VerdictBadge verdict={item.verdict} />
                    </Td>
                    <Td className="text-right tabular-nums">{fmt.number(item.scan_count)}</Td>
                    <Td className="text-ink-muted">
                      {item.last_scan_at === null || item.last_scan_at === undefined
                        ? '—'
                        : fmt.dateTime(item.last_scan_at)}
                    </Td>
                    <Td className="text-ink-muted">
                      {item.activated_at === null || item.activated_at === undefined
                        ? '—'
                        : fmt.dateTime(item.activated_at)}
                    </Td>
                    <Td className="text-ink-muted">{fmt.dateTime(item.created_at)}</Td>
                    <Td className="text-right whitespace-nowrap">
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={qrBusy !== null}
                        onClick={() => {
                          void bajarQr(item.code);
                        }}
                      >
                        {qrBusy === item.code
                          ? t('labels.units.qr.downloading')
                          : t('labels.units.column.qr')}
                      </Button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <CardBody className="flex items-center justify-center border-t border-border">
              {labels.hasMore ? (
                <Button size="sm" onClick={labels.loadMore} disabled={labels.isLoadingMore}>
                  {labels.isLoadingMore ? t('state.loadingMore') : t('state.loadMore')}
                </Button>
              ) : (
                <p className="text-xs text-ink-subtle">{t('state.allLoaded')}</p>
              )}
            </CardBody>
          </>
        )}
      </Card>
    </div>
  );
}
